import React, { useState } from "react"
import { motion } from "framer-motion"
import { useTranslation } from "react-i18next"
import Statusbadge from "../Statusbadge"
import TablePagination from "../../../../../shared/components/TablePagination"

const fmtDate = (val, withTime = false) => {
  if (!val) return ""
  const d = new Date(val)
  if (Number.isNaN(d.getTime())) return ""
  const date = d.toLocaleDateString("ru-RU")
  if (!withTime) return date
  return `${date} ${d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}`
}

const moduleLabel = (key) =>
  ({ hotel: "Отель", transport: "Транспорт", transfer: "Трансфер", insurance: "Страх" })[key] || key

const fmt = (val) => {
  if (val === null || val === undefined) return ""
  if (typeof val === "boolean") return val ? "Да" : "Нет"
  if (typeof val === "object") return "" // защита от крэша
  return String(val)
}

const fmtMoney = (amount, currency) =>
  [amount != null ? String(amount) : null, currency].filter(Boolean).join(" ")

const IconRefresh = ({ className = "w-3 h-3" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
)

const IconTrash = ({ className = "w-3 h-3" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
)

const IconChat = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  </svg>
)

const IconChevron = ({ className = "w-3 h-3" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
)

const VISIBLE_CLIENTS = 2

const ClientsCell = ({ clients, t }) => {
  const [expanded, setExpanded] = useState(false)

  const names = (clients || [])
    .map((c) => `${c.name ?? ""} ${c.surname ?? ""}`.trim())
    .filter(Boolean)

  if (names.length === 0) return null

  const hasMore = names.length > VISIBLE_CLIENTS
  const shown = expanded ? names : names.slice(0, VISIBLE_CLIENTS)

  return (
    <>
      {shown.map((name, i) => (
        <div key={i}>{name}</div>
      ))}

      {hasMore && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setExpanded((v) => !v)
          }}
          className="mt-0.5 text-[11px] font-semibold text-blue-600 hover:text-blue-700 transition-colors"
        >
          {expanded
            ? t("filter.hide")
            : t("admin.show_more_with_count", {
                count: names.length - VISIBLE_CLIENTS,
              })}
        </button>
      )}
    </>
  )
}

const CommentCell = ({ text, t }) => {
  const [expanded, setExpanded] = useState(false)

  if (!text) return null

  const isLong = text.length > 90

  return (
    <div
      className={`text-xs text-gray-600 max-w-[200px] ${isLong ? "cursor-pointer" : ""}`}
      title={text}
      onClick={(e) => {
        if (!isLong) return
        e.stopPropagation()
        setExpanded((v) => !v)
      }}
    >
      <div
        style={
          !expanded
            ? {
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
            : { whiteSpace: "pre-wrap" }
        }
      >
        {text}
      </div>

      {isLong && (
        <div className="mt-0.5 text-[11px] font-semibold text-blue-600">
          {expanded ? t("filter.hide") : t("admin.show_full")}
        </div>
      )}
    </div>
  )
}

const ProgressCircle = ({ percent, label, size = 32 }) => {
  const r = (size - 4) / 2
  const circ = 2 * Math.PI * r
  const pct = Math.min(Math.max(percent || 0, 0), 100)
  const dash = (pct / 100) * circ
  const color = pct >= 99 ? "#22c55e" : pct > 20 ? "#f59e0b" : "#ef4444"

  return (
    <div className="flex flex-col items-center gap-0.5" title={`${label}: ${pct}%`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={3} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={3}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="7"
          fill={color}
          fontWeight="600"
        >
          {Math.round(pct)}%
        </text>
      </svg>
      <span className="text-[9px] text-gray-400 leading-none max-w-[40px] text-center truncate">
        {label}
      </span>
    </div>
  )
}

const RelatedBundlesCell = ({ bundles }) => {
  if (!bundles?.length) return null
  return (
    <div className="flex flex-col gap-0.5">
      {bundles.map((b) => (
        <span key={b.id} className="text-[11px] text-blue-600 font-medium">
          #{b.order || b.reservation || b.id}
        </span>
      ))}
    </div>
  )
}

const VISIBLE_AVATARS = 4
const AVATAR_COLORS = [
  "#6366f1",
  "#06b6d4",
  "#f59e0b",
  "#ec4899",
  "#10b981",
  "#8b5cf6",
  "#ef4444",
  "#14b8a6",
]

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?"

const colorFor = (key) =>
  AVATAR_COLORS[
    Math.abs(
      String(key)
        .split("")
        .reduce((a, c) => a + c.charCodeAt(0), 0)
    ) % AVATAR_COLORS.length
  ]

const ViewersStack = ({ viewers }) => {
  if (!viewers?.length) return null
  const shown = viewers.slice(0, VISIBLE_AVATARS)
  const extra = viewers.length - shown.length

  return (
    <div className="flex items-center" title={viewers.map((v) => v.name).join(", ")}>
      {shown.map((v, i) => (
        <div
          key={v.id ?? i}
          style={{
            backgroundColor: v.color || colorFor(v.id ?? v.name),
            marginLeft: i === 0 ? 0 : -8,
            zIndex: shown.length - i,
          }}
          className="relative w-6 h-6 rounded-full border-2 border-white flex items-center justify-center
            text-[9px] font-bold text-white shadow-sm"
        >
          {v.avatar_url ? (
            <img
              src={v.avatar_url}
              alt={v.name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            initials(v.name)
          )}
        </div>
      ))}
      {extra > 0 && (
        <div
          style={{ marginLeft: -8 }}
          className="relative w-6 h-6 rounded-full border-2 border-white bg-gray-200
            flex items-center justify-center text-[9px] font-bold text-gray-600 shadow-sm"
        >
          +{extra}
        </div>
      )}
    </div>
  )
}

const RequestsTable = ({
  data,
  columns,
  ordCols,
  selectedRows,
  allSelected,
  onToggleAll,
  onToggleRow,
  onDelete,
  onEdit,
  onRefresh,
  page,
  total,
  totalPages,
  onPageChange,
  perPage,
  perPageOptions,
  onPerPageChange,
}) => {
  const { t } = useTranslation()

  const [expandedRows, setExpandedRows] = useState(() => new Set())

  const toggleChildren = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const renderCell = (col, row, isChild = false) => {
    switch (col) {
      case "id": {
        const hasChildren = !isChild && (row.children?.length ?? 0) > 0
        const isExpanded = hasChildren && expandedRows.has(row.id)

        return (
          <div
            className={`min-w-[100px] flex items-center gap-1.5 flex-wrap ${isChild ? "pl-4" : ""}`}
          >
            {isChild && <span className="text-gray-300 text-xs select-none">↳</span>}

            {!isChild && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  if (hasChildren) toggleChildren(row.id)
                }}
                disabled={!hasChildren}
                title={
                  hasChildren
                    ? t("admin.related_bundles", { defaultValue: "Дочерние заявки" })
                    : undefined
                }
                className={`inline-flex items-center justify-center w-5 h-5 rounded-md border flex-shrink-0 transition-colors
                  ${!hasChildren ? "invisible" : ""}
                  ${
                    isExpanded
                      ? "bg-blue-50 border-blue-200 text-blue-600"
                      : "bg-gray-50 border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  }`}
              >
                <motion.span
                  animate={{ rotate: isExpanded ? 90 : 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex"
                >
                  <IconChevron />
                </motion.span>
              </button>
            )}

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                window.location.href = `/book/bundle/edit/${row.id}`
              }}
              className={`inline-flex items-center gap-1.5 rounded-lg text-white font-bold
                shadow-sm hover:shadow-md hover:brightness-105 active:scale-95 transition-all
                bg-gradient-to-br from-rose-400 to-orange-400
                ${isChild ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"}`}
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              #{row.display_nr ?? row.order ?? row.id}
            </button>

            {!isChild && (
              <span
                className={`text-[10px] font-semibold text-gray-400 bg-gray-50 border border-gray-200 rounded-full px-1.5 leading-4
                  ${!hasChildren ? "invisible" : ""}`}
              >
                +{hasChildren ? row.children.length : 0}
              </span>
            )}

            <ViewersStack viewers={row.active_viewers} />
          </div>
        )
      }

      case "order":
        return <span className="text-xs text-gray-700">{row.order}</span>

      case "status":
        return (
          <div className="flex flex-col gap-1 min-w-[110px]">
            <Statusbadge
              label={row.status_name}
              background={row.status_color}
              color={row.status_text_color}
            />
            {row.onrequest && (
              <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] bg-yellow-50 text-yellow-700 border border-yellow-200">
                По запросу
              </span>
            )}
            {row.check_mode && (
              <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] bg-purple-50 text-purple-700 border border-purple-200">
                Режим проверки
              </span>
            )}
            {row.prebooking && (
              <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] bg-sky-50 text-sky-700 border border-sky-200">
                Предбронирование
              </span>
            )}
            {row.room_status_name && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200">
                <span className="text-slate-400 font-normal">{t("filter.room_status")}:</span>
                {row.room_status_name}
              </span>
            )}
          </div>
        )

      // ── Даты ─────────────────────────────────────────────────────────────
      case "created_at":
        return (
          <div className="text-xs text-gray-600 whitespace-nowrap">
            <div>{fmtDate(row.created_at, true)}</div>
            {row.prebooking_date && (
              <div className="text-gray-400 text-[10px]">
                Пребук: {fmtDate(row.prebooking_date)}
              </div>
            )}
          </div>
        )

      case "from_date":
        return (
          <span className="text-xs font-medium text-gray-800 whitespace-nowrap">
            {fmtDate(row.from_date)}
          </span>
        )

      case "to_date":
        return (
          <span className="text-xs font-medium text-gray-800 whitespace-nowrap">
            {fmtDate(row.to_date)}
          </span>
        )

      case "nights":
        return <span className="text-xs text-gray-600">{row.nights ?? ""}</span>

      // ── Туристы ───────────────────────────────────────────────────────────
      // col=client → бэкенд присылает row.clients: [{name, surname}]
      case "client":
        return <ClientsCell clients={row.clients} t={t} />

      // col=tourist_name → бэкенд присылает row.tourist_names (строка) + tourist_count
      case "tourist_name":
        return (
          <div className="text-xs text-gray-800 min-w-[110px]">
            {row.tourist_names && <div className="font-medium">{row.tourist_names}</div>}
            {row.tourist_count != null && (
              <div className="text-gray-400 text-[10px]">{row.tourist_count} чел.</div>
            )}
          </div>
        )

      case "count_person":
        return <span className="text-xs text-gray-600">{row.tourist_count ?? ""}</span>

      case "company_name": {
        const hasBg = !!row.row_style?.background
        return (
          <div className="text-xs min-w-[150px]">
            {row.company_name && (
              <a
                href={`/admin/companies/${row.company_id}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className={`font-semibold underline underline-offset-2 ${
                  hasBg
                    ? "text-blue-900 decoration-blue-900/40 hover:text-blue-950"
                    : "text-blue-600 decoration-blue-300 hover:text-blue-700"
                }`}
              >
                {row.company_name}
              </a>
            )}
            {row.user_name && (
              <div className={`mt-0.5 ${hasBg ? "text-gray-800" : "text-gray-700"}`}>
                {row.user_name}
              </div>
            )}
            {row.user_email && (
              <div className={`text-[10px] ${hasBg ? "text-gray-700" : "text-gray-400"}`}>
                {row.user_email}
              </div>
            )}
            {row.touroperator_name && (
              <div className={`text-[10px] mt-0.5 ${hasBg ? "text-gray-700" : "text-gray-500"}`}>
                Тур-оператор - {row.touroperator_name}
              </div>
            )}
            {row.company_type_name && (
              <span
                className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                style={{
                  backgroundColor: hasBg
                    ? "rgba(255,255,255,0.85)"
                    : row.company_type_color || "#f3f4f6",
                  color: "#1f2937",
                }}
              >
                {row.company_type_name}
              </span>
            )}
          </div>
        )
      }

      case "agency":
        return (
          <div className="text-xs text-gray-700 min-w-[140px]">
            {row.company_name && <div className="font-medium">{row.company_name}</div>}
            {row.user_name && <div className="text-gray-500">{row.user_name}</div>}
            {row.user_email && <div className="text-gray-400 text-[10px]">{row.user_email}</div>}
          </div>
        )

      case "user_name":
        return (
          <div className="text-xs text-gray-700 min-w-[120px]">
            <div className="font-medium">{row.user_name}</div>
            {row.user_email && <div className="text-gray-400">{row.user_email}</div>}
          </div>
        )

      case "moderator":
        return <span className="text-xs text-gray-700">{row.moderator_name}</span>

      case "country":
        return (
          <div className="text-xs text-gray-700 min-w-[90px]">
            {row.state_name && <div className="font-medium">{row.state_name}</div>}
            {row.resort_name && <div className="text-gray-500">{row.resort_name}</div>}
          </div>
        )

      case "hotel":
        return (
          <div className="text-xs text-gray-700 min-w-[150px] max-w-[220px]">
            {row.hotel_name && <div className="font-medium">{row.hotel_name}</div>}
            {row.food_name && <div className="text-gray-500">{row.food_name}</div>}
            {row.type_room_name && <div className="text-gray-500">{row.type_room_name}</div>}
            {row.hotel_allocated && (
              <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700">
                Размещён
              </span>
            )}
          </div>
        )

      case "transport_info":
        return (
          <div className="text-xs min-w-[130px]">
            {row.transport_printed && (
              <span className="text-emerald-600" title="выписан">
                ✓{" "}
              </span>
            )}
            {row.transport_charter_depart && (
              <div className="font-medium text-gray-700">{row.transport_charter_depart}</div>
            )}
            {row.transport_depart_line && (
              <div className="text-gray-500">{row.transport_depart_line}</div>
            )}
            {row.transport_charter_retur && (
              <div className="font-medium text-gray-700 mt-0.5">{row.transport_charter_retur}</div>
            )}
            {row.transport_retur_line && (
              <div className="text-gray-500">{row.transport_retur_line}</div>
            )}
            {row.transport_badge && (
              <span
                className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium
            ${row.transport_badge.type === "canceled" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}
              >
                {row.transport_badge.label}
              </span>
            )}
          </div>
        )

      case "flights":
        return (
          <div className="text-xs text-gray-600">
            {row.flight_depart_id > 0 && <div>↑ #{row.flight_depart_id}</div>}
            {row.flight_return_id > 0 && <div>↓ #{row.flight_return_id}</div>}
          </div>
        )

      // ── Места ─────────────────────────────────────────────────────────────
      case "depart_places":
        return (
          <div className="text-xs text-gray-600">
            {row.depart_places != null && <div>↑ {row.depart_places}</div>}
            {row.retur_places != null && <div>↓ {row.retur_places}</div>}
          </div>
        )

      // ── Суммы ─────────────────────────────────────────────────────────────
      case "company_sum":
        return (
          <span className="text-xs font-medium text-gray-800 whitespace-nowrap">
            {fmtMoney(row.company_sum, row.valute_name)}
          </span>
        )

      // col=costs — бэкенд пишет agency_amount + agency_paid в тот же row
      case "costs":
        return (
          <div className="text-xs min-w-[100px]">
            <div className="font-medium text-gray-800">{fmt(row.agency_amount)}</div>
            {row.agency_paid != null && (
              <div className="text-gray-500">Опл: {fmt(row.agency_paid)}</div>
            )}
            {row.agency_debt != null && (
              <div className={row.agency_debt > 0 ? "text-red-500" : "text-emerald-600"}>
                Долг: {fmt(row.agency_debt)}
              </div>
            )}
          </div>
        )

      case "agency_amount":
        return <span className="text-xs font-medium text-gray-800">{fmt(row.agency_amount)}</span>

      case "agency_paid":
        return (
          <div className="text-xs">
            <div className="font-medium text-gray-800">{fmt(row.agency_paid)}</div>
            {row.agency_paid_pct != null && (
              <div className="text-gray-400 text-[10px]">{row.agency_paid_pct}%</div>
            )}
          </div>
        )

      case "agency_debt":
        return (
          <span
            className={`text-xs font-medium ${row.agency_debt > 0 ? "text-red-500" : "text-emerald-600"}`}
          >
            {fmt(row.agency_debt)}
          </span>
        )

      case "operator_debt":
        return (
          <span
            className={`text-xs font-medium ${row.operator_debt > 0 ? "text-red-500" : "text-emerald-600"}`}
          >
            {fmt(row.operator_debt)}
          </span>
        )

      case "profit":
        return (
          <span
            className={`text-xs font-medium ${row.profit < 0 ? "text-red-500" : "text-emerald-700"}`}
          >
            {fmt(row.profit)}
          </span>
        )

      case "transport_profit":
        return (
          <span
            className={`text-xs font-medium ${row.transport_profit < 0 ? "text-red-500" : "text-emerald-700"}`}
          >
            {fmt(row.transport_profit)}
          </span>
        )

      case "client_costs":
        return <span className="text-xs text-gray-800">{fmt(row.client_costs)}</span>

      // col=operator_costs → бэкенд: { total, paid } в row.total / row.paid
      // НО: serialize_bundle делает result.merge!(operator_costs_for) — ключи попадают в root
      case "operator_costs":
        return (
          <div className="text-xs min-w-[80px]">
            <div className="font-medium text-gray-800">{fmt(row.total)}</div>
            {row.paid != null && <div className="text-gray-500">Опл: {fmt(row.paid)}</div>}
          </div>
        )

      // col=hotel_operator_costs → { total, paid } под ключом hotel_operator_costs
      case "hotel_operator_costs":
        return (
          <div className="text-xs min-w-[80px]">
            <div className="font-medium text-gray-800">{fmt(row.hotel_operator_costs?.total)}</div>
            {row.hotel_operator_costs?.paid != null && (
              <div className="text-gray-500">Опл: {fmt(row.hotel_operator_costs.paid)}</div>
            )}
          </div>
        )

      // col=transfer_operator_costs → { total, paid }
      case "transfer_operator_costs":
        return (
          <div className="text-xs min-w-[80px]">
            <div className="font-medium text-gray-800">
              {fmt(row.transfer_operator_costs?.total)}
            </div>
            {row.transfer_operator_costs?.paid != null && (
              <div className="text-gray-500">Опл: {fmt(row.transfer_operator_costs.paid)}</div>
            )}
          </div>
        )

      // col=agency_payments_local_amounts → ключ sum_paid_local
      case "agency_payments_local_amounts":
        return <span className="text-xs text-gray-800">{fmt(row.sum_paid_local)}</span>

      // col=agency_payments_rate → ключ payment_rates
      case "agency_payments_rate":
        return <span className="text-xs text-gray-600">{fmt(row.payment_rates)}</span>

      // col=agency_payments_notes → ключ payment_notes
      case "agency_payments_notes":
        return <span className="text-xs text-gray-600">{fmt(row.payment_notes)}</span>

      // col=type_payment_from_client → ключ payments_by_type (объект, не примитив!)
      case "type_payment_from_client": {
        const pbt = row.payments_by_type
        if (!pbt) return null
        const labels = {
          transfer: "Перевод",
          check_cash: "Нал.чек",
          bank_payment: "Банк",
          bank_card: "Карта",
          terminal: "Терминал",
          no_check_cash: "Нал.",
        }
        return (
          <div className="text-xs space-y-0.5 min-w-[100px]">
            {Object.entries(pbt)
              .filter(([, v]) => v > 0)
              .map(([k, v]) => (
                <div key={k}>
                  <span className="text-gray-400">{labels[k] ?? k}: </span>
                  <span className="text-gray-700 font-medium">{v}</span>
                </div>
              ))}
          </div>
        )
      }

      case "providers":
        return (
          <div className="text-xs space-y-0.5 min-w-[150px]">
            {(row.providers || []).map((p) => (
              <div key={p.module_id}>
                <span className="text-gray-400">{moduleLabel(p.module_key)}:</span>{" "}
                <span className="text-gray-600">{p.provider}</span>
                {p.order && <span className="text-gray-700 font-medium"> — {p.order}</span>}
                {p.status === "is_canceled" && (
                  <span className="ml-1 text-[10px] text-red-500">отменён</span>
                )}
              </div>
            ))}
          </div>
        )

      case "comment":
        return <CommentCell text={row.comment} t={t} />

      case "comment_admin":
      case "admin_comment":
        return <span className="text-xs text-gray-600">{row.comment_admin}</span>
      case "notification":
        return (
          <div className="flex items-center gap-1.5">
            <div
              className="relative inline-flex cursor-pointer"
              onClick={(e) => {
                e.stopPropagation()
                window.EventBus.emit("modal:chatbox:open", {
                  locale: I18n.locale,
                  bundleId: row.id,
                })
              }}
            >
              <span
                className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors
                  ${row.unread_comments > 0 ? "bg-blue-50 text-blue-600" : "bg-gray-50 text-gray-300"}`}
              >
                <IconChat />
              </span>
              {row.unread_comments > 0 && (
                <span
                  className="absolute -top-1 -right-1 inline-flex items-center justify-center
                    min-w-[16px] h-4 px-1 rounded-full bg-blue-500 text-white text-[9px] font-bold leading-none"
                >
                  {row.unread_comments}
                </span>
              )}
            </div>
          </div>
        )

      // ── Прогресс оплаты ───────────────────────────────────────────────────
      case "payment_progress":
        return (
          <div className="flex items-start gap-2 flex-wrap">
            {(row.payment_progress || []).map((c) => (
              <ProgressCircle key={c.key} percent={c.percent} label={c.label} />
            ))}
          </div>
        )

      // ── Заметки по услугам ────────────────────────────────────────────────
      // service_notes → { hotel, transport, transfer, insurance } — объект!
      case "service_notes": {
        const sn = row.service_notes
        if (!sn || typeof sn !== "object") return null
        const snLabels = {
          hotel: "Отель",
          transport: "Транспорт",
          transfer: "Трансфер",
          insurance: "Страх",
        }
        return (
          <div className="text-xs space-y-0.5 min-w-[120px]">
            {Object.entries(sn).map(([k, v]) =>
              v ? (
                <div key={k}>
                  <span className="text-gray-400">{snLabels[k] ?? k}: </span>
                  <span className="text-gray-600">{v}</span>
                </div>
              ) : null
            )}
          </div>
        )
      }

      // ── Связанные заявки ───────────────────────────────────────────────────
      case "related_bundles":
        return <RelatedBundlesCell bundles={row.related_bundles} />

      // ── Кто сейчас просматривает заявку (presence) ───────────────────────
      case "viewers":
      case "active_viewers":
        return <ViewersStack viewers={row.active_viewers || row.viewers} />

      // ── Источник / архив / булевы ──────────────────────────────────────────
      case "referer":
        return <span className="text-xs text-gray-600">{row.referer}</span>

      case "onrequest":
        return row.onrequest ? (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-yellow-50 text-yellow-700 border border-yellow-200">
            Да
          </span>
        ) : null

      case "check_mode":
        return row.check_mode ? (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-50 text-purple-700 border border-purple-200">
            Да
          </span>
        ) : null

      case "prebooking":
        return (
          <div className="text-xs">
            {row.prebooking ? (
              <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">
                Да
              </span>
            ) : null}
            {row.prebooking_date && (
              <div className="text-gray-400 text-[10px] mt-0.5">{fmtDate(row.prebooking_date)}</div>
            )}
          </div>
        )

      case "competitor_price_request":
        return row.competitor_price_request ? (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-orange-50 text-orange-700 border border-orange-200">
            Да
          </span>
        ) : null

      case "not_show_price":
        return row.not_show_price ? (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600">
            Скрыта
          </span>
        ) : null

      case "is_archive":
        return (
          <div className="text-xs space-y-0.5">
            {row.is_archive && (
              <span className="block px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Архив</span>
            )}
            {row.is_delete && (
              <span className="block px-1.5 py-0.5 rounded bg-red-50 text-red-600">Удалён</span>
            )}
          </div>
        )

      case "room_status":
        return (
          <span className="text-xs text-gray-600">{row.room_status_name || row.room_status}</span>
        )

      case "reservation":
        return (
          <div className="text-xs text-gray-700">
            <div>{row.reservation}</div>
            {row.contract_nr && <div className="text-gray-400">Договор: {row.contract_nr}</div>}
          </div>
        )

      case "service_type":
        return <span className="text-xs text-gray-600">{fmt(row.service_type)}</span>

      default: {
        const val = row[col]
        if (val !== null && typeof val === "object") return null
        return <span className="text-xs text-gray-600">{fmt(val)}</span>
      }
    }
  }

  const from = total === 0 ? 0 : (page - 1) * perPage + 1
  const to = Math.min(page * perPage, total)

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="bg-white rounded-2xl overflow-hidden"
      style={{
        boxShadow:
          "0 4px 6px -1px rgba(0,0,0,0.07),0 10px 30px -4px rgba(0,0,0,0.12),0 0 0 1px rgba(0,0,0,0.06)",
      }}
    >
      <div className="overflow-x-auto" style={{ minHeight: perPage * 57 + 48 }}>
        <table className="w-full text-left">
          <thead>
            <tr
              style={{
                background: "linear-gradient(to bottom,#f8fafc,#f1f5f9)",
                borderBottom: "2px solid #e2e8f0",
              }}
            >
              <th className="w-10 px-4 py-3.5">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={onToggleAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              {ordCols.map((colId) => {
                const col = columns.find((c) => c.id === colId)
                return (
                  <th
                    key={colId}
                    className="px-4 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {col?.label ?? colId}
                  </th>
                )
              })}
              <th className="px-4 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={ordCols.length + 2} className="py-16 text-center text-gray-400">
                  <svg
                    className="w-10 h-10 mx-auto mb-3 text-gray-200"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="text-sm">Нет записей</p>
                </td>
              </tr>
            ) : (
              data.map((row, idx) => {
                const hasChildren = (row.children?.length ?? 0) > 0
                const isExpanded = hasChildren && expandedRows.has(row.id)

                return (
                  <React.Fragment key={row.id}>
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.15, ease: "easeOut", delay: idx * 0.025 }}
                      style={
                        row.row_style?.background
                          ? {
                              backgroundColor: row.row_style.background,
                              color: row.row_style.color,
                            }
                          : undefined
                      }
                      className={`border-b border-gray-50 transition-colors hover:bg-blue-50/30
                        ${selectedRows.includes(row.id) ? "bg-blue-50/50" : !row.row_style?.background && idx % 2 === 1 ? "bg-slate-50/40" : ""}
                        ${row.row_style?.class ? `row-${row.row_style.class}` : ""}`}
                    >
                      <td className="px-4 py-3.5 align-top">
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(row.id)}
                          onChange={() => onToggleRow(row.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      {ordCols.map((colId) => (
                        <td key={colId} className="px-4 py-3.5 align-top">
                          {renderCell(colId, row)}
                        </td>
                      ))}
                      <td className="px-4 py-3.5 align-top">
                        <div className="flex items-center gap-1">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => {
                              window.location.href = `/book/bundle/log/${row.id}`
                            }}
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors"
                          >
                            <IconRefresh className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => onDelete(row.id)}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <IconTrash className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>

                    {/* Дочерние заявки — отрисовываются той же renderCell
                        и теми же ordCols, что и родитель, т.к. бэкенд
                        отдаёт им идентичный набор полей. */}
                    {isExpanded &&
                      row.children.map((child) => (
                        <motion.tr
                          key={`child-${child.id}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.12, ease: "easeOut" }}
                          style={
                            child.row_style?.background
                              ? {
                                  backgroundColor: child.row_style.background,
                                  color: child.row_style.color,
                                }
                              : undefined
                          }
                          className={`border-b border-gray-50 transition-colors hover:bg-blue-50/20
                            ${!child.row_style?.background ? "bg-slate-50/70" : ""}
                            ${selectedRows.includes(child.id) ? "bg-blue-50/50" : ""}`}
                        >
                          <td className="px-4 py-2.5 align-top">
                            <input
                              type="checkbox"
                              checked={selectedRows.includes(child.id)}
                              onChange={() => onToggleRow(child.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          {ordCols.map((colId) => (
                            <td key={colId} className="px-4 py-2.5 align-top">
                              {renderCell(colId, child, true)}
                            </td>
                          ))}
                          <td className="px-4 py-2.5 align-top">
                            <div className="flex items-center gap-1">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => {
                                  window.location.href = `/book/bundle/log/${child.id}`
                                }}
                                className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors"
                              >
                                <IconRefresh className="w-4 h-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => onDelete(child.id)}
                                className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <IconTrash className="w-4 h-4" />
                              </motion.button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        onPageChange={onPageChange}
        totalCount={total}
        page={page}
        from={from}
        to={to}
        totalPages={totalPages}
        perPage={perPage}
        perPageOptions={perPageOptions}
        onPerPageChange={onPerPageChange}
      />
    </motion.div>
  )
}

export default RequestsTable
